<script setup lang='ts'>
import { Heart, Share2 } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Language } from '@/types/locale.ts';

import TheStarRating from '@/components/mode-list/TheStarRating.vue';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { formatDisplayRating } from '@/utils/utils';

import DesktopGallery from './DesktopGallery.vue';
import Popover from './Popover.vue';

const props = defineProps<{
  shareLinks: Array<{ name: string; href: string; imgSrc: string }>;
  desktopHeaderData: HeaderProps;
}>();

const emits = defineEmits<{
  toggleFavorite: [void];
  scrollToFairguestWidget: [void];
}>();

const { t } = useI18n();

const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration, componentsBaseUrl } = storeToRefs(globalConfigurationStore);
const showFavorites = computed(() => globalConfiguration.value?.options?.hasFavorites !== false);

type HeaderProps = {
  title: string;
  source: string;
  hasWidget: boolean;
  rating: {
    ratingReviewCount: number;
    rating: string;
    ratingSource: string;
  } | null;
  images: Array<{ credit: string | null; lang: Language; name: string; url: string }>;
};

const rating = computed(() => props.desktopHeaderData.rating?.rating ?? '0');
const displayedRating = computed(() => formatDisplayRating(+rating.value));
</script>

<template>
  <section class="desktop_header normal_spacing" :aria-label="t('aria.presentation')">
    <h1 class="title">
      {{ props.desktopHeaderData.title }}
    </h1>

    <div class="header_details">
      <div v-if="props.desktopHeaderData.rating" class="rating_wrapper">
        <template v-if="props.desktopHeaderData.rating.ratingSource === 'FAIRGUEST'">
          <TheStarRating :rating="displayedRating" />
          <p class="rating">
            {{ props.desktopHeaderData.rating.rating }} / 10
          </p>
          <div
            role="link" class="reviews_count" tabindex="0" @click="emits('scrollToFairguestWidget')"
            @keydown.enter="emits('scrollToFairguestWidget')"
          >
            {{ props.desktopHeaderData.rating.ratingReviewCount }} {{ t('content.reviews') }}
          </div>
        </template>
        <template v-else>
          <div class="external-rating">
            <svg class="external-star" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
            </svg>
            <p class="rating">
              {{ props.desktopHeaderData.rating.rating }}
            </p>
            <span class="review-count">({{ props.desktopHeaderData.rating.ratingReviewCount }})</span>
            <img v-if="props.desktopHeaderData.rating.ratingSource === 'GOOGLE'" :src="`${componentsBaseUrl}/logo_google.jpg`" height="20" width="20" alt="source des avis" loading="lazy">
            <Popover :title="t('rating.title')" :content="t('rating.content')">
              <template #button>
                <p class="rating_note">
                  {{ t('rating.title') }}
                </p>
              </template>
            </Popover>
          </div>
        </template>
      </div>
      <div class="actions_wrapper">
        <Popover :items="props.shareLinks" :title="t('popover.share')">
          <template #button>
            <Share2 :size="20" aria-hidden="true" style="position:relative;right:1px;top:1px;" />
            <span style="margin-left:0.5rem;font-weight:var(--yb-text-bold);">{{ t('cta.share') }}</span>
          </template>
        </Popover>
        <button
          v-if="showFavorites" id="favorites" type="button" :aria-label="t('cta.add_to_favorites')"
          @click="emits('toggleFavorite')"
        >
          <span style="display:flex;align-items:center;margin-right:0.5rem;font-size:var(--yb-text-xl);">
            <Heart :size="20" aria-hidden="true" style="position:relative;top:1px;" />
          </span>
          <span id="favorites_label">{{ t('cta.add_to_favorites') }}</span>
        </button>
      </div>
    </div>
    <DesktopGallery :images="props.desktopHeaderData.images" :has-widget="props.desktopHeaderData.hasWidget" container-height="400px" gap=".5rem" />
    <!-- <div class="img_container normal_spacing">
      <img
        v-for="(image, index) in props.desktopHeaderData.images"
        :key="index"
        :src="image.url"
        :fetchpriority="index === 0 ? 'high' : 'auto'"
        :loading="index === 0 ? 'eager' : 'lazy'"
        :alt="`Image de l'activité ${index + 1}`"
      >
    </div> -->
  </section>
</template>

<style scoped>
.desktop_header {
  display: none;

  @media screen and (width > 767px) {
    min-height: 495px;
    display: block;
  }

  .title {
    font-size: 35px;
    font-weight: var(--yb-text-bold);
    margin-bottom: var(--yb-radius-sm);
  }

  .img_container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0px, 1fr));
    gap: var(--yb-spacing-sm);

    img {
      width: 100%;
      height: 400px;
      object-fit: cover;
    }
  }

  .header_details {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--yb-spacing-sm);
  }

  .rating_wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    color: var(--yb-color-black);
    gap: var(--yb-spacing-sm);

    .rating {
      font-weight: var(--yb-text-bold);
      font-size: 16px;
    }

    .reviews_count {
      font-weight: var(--yb-text-light);
      font-size: 12px;
      text-decoration: underline;
      cursor: pointer;
    }

    .external-rating {
      display: flex;
      align-items: center;
      gap: 4px;

      .rating_note {
        color: var(--yb-color-black);
        font-weight: 400;
        font-size: 14px;
        cursor: pointer;
        text-decoration: underline;
        text-decoration-color: var(--yb-color-black);
      }
    }

    .external-star {
      width: 20px;
      height: 20px;
      fill: #ff9800;
      flex-shrink: 0;
    }

    .review-count {
      font-weight: var(--yb-text-light);
      font-size: 14px;
    }
  }

  .actions_wrapper {
    display: flex;
    align-items: center;
    gap: var(--yb-spacing-xs);
    margin-left: auto;

    #favorites {
      display: flex;
      align-items: center;
      appearance: none;
      text-decoration: none;
      background-color: transparent;
      color: var(--yb-color-black);
      text-align: center;
      font-size: var(--yb-text-sm);
      font-weight: var(--yb-text-bold);
      border: 1px solid transparent;
      cursor: pointer;

      @media screen and (width < 768px) {
        display: none;
      }
    }

    #favorites span svg {
      stroke: var(--yb-color-black);
      fill: var(--yb-color-white);
      transition: all 0.2s ease-out;
    }

    #favorites:hover #favorites_label {
      color: var(--yb-color-primary);
    }

    #favorites:hover span svg {
      stroke: var(--yb-color-primary);
      fill: var(--yb-color-primary);
    }

    #favorites.selected span svg {
      stroke: var(--yb-color-primary);
      fill: var(--yb-color-primary);
      fill-opacity: 1;
    }
  }
}
</style>
